import { LoaderFunction, redirect } from "react-router-dom";
import { IMPORT_JOB_STORAGE_KEY, routes } from "~/lib/constants";
import { authActions } from "~/redux/slice/auth";
import store, { persistor, type ReduxState } from "~/redux/store";

/** Give up waiting for rehydration rather than hanging the logout. */
const REHYDRATE_TIMEOUT_MS = 3000;

const isRehydrated = () =>
  Boolean((store.getState() as ReduxState).persistedReducer?._persist?.rehydrated);

/**
 * The router is created at module scope, so on a cold load of /logout this loader
 * runs *before* redux-persist has rehydrated. Clearing the auth slice at that
 * point is a no-op on empty state, and the subsequent REHYDRATE puts the stored
 * session straight back — logout silently does nothing. Waiting for rehydration
 * first makes the clear apply to real state on every entry path.
 */
const waitForRehydration = () =>
  new Promise<void>((resolve) => {
    if (isRehydrated()) return resolve();

    const timer = setTimeout(() => {
      unsubscribe();
      resolve();
    }, REHYDRATE_TIMEOUT_MS);

    const unsubscribe = store.subscribe(() => {
      if (!isRehydrated()) return;
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
  });

export const LogoutLoader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);
  const redirectTo = url.searchParams.get("redirectTo") || "/";

  await waitForRehydration();

  store.dispatch(authActions.clearTokens());

  // Logout is a client-side redirect, so localStorage survives into the next
  // session. An in-flight import job id left behind would be picked up by the
  // next user on this browser, who would then be shown someone else's document.
  try {
    localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode / blocked) — nothing to clear.
  }

  // Commit the cleared state before navigating, so a browser closed immediately
  // after logout does not leave a usable token on disk.
  try {
    await persistor.flush();
  } catch {
    // Flush is best-effort; the state is already cleared in memory.
  }

  return redirect(routes.login() + `?redirectTo=${redirectTo}`);
};

export default function Logout() {
  return <div>logout</div>;
}
